/**
 * Global theme carousel web component.
 *
 * Usage:
 * When deploying this component, use the following HTML markup:
 *
 * <uwp-carousel
 *   setting-autoplay="{{ section.settings.autoplay }}"
 *   setting-autoplay-speed="{{ section.settings.autoplay_speed | json }}"
 *   setting-pagination="{{ section.settings.pagination }}"
 *   setting-arrows="{{ section.settings.arrows }}"
 * >
 *   <div class="splide__track">
 *     <div class="splide__list">
 *       <div class="splide__slide"></div>
 *     </div>
 *   </div>
 * </uwp-carousel>
 */

import Splide from "@splidejs/splide";
import type {
  Options as SplideOptions,
  Splide as SplideSlider,
} from "@splidejs/splide";
import { debounce } from "@helpers/utils";

export interface ICarousel {
  createCarousel: () => void;
  validateMarkup: () => void;
}

export class Carousel extends HTMLElement implements ICarousel {
  private config: SplideOptions;

  private carousel: SplideSlider | null;

  constructor() {
    super();
    this.config = {};
    this.carousel = null;
    this.classList.add("splide");
    this.handleResizeEvents = this.handleResizeEvents.bind(this);
  }

  connectedCallback(): void {
    this.validateMarkup();
    this.initializeConfig();
    this.createCarousel();
    this.handleResizeEvents();
  }

  disconnectedCallback(): void {
    if (this.carousel) {
      this.carousel.destroy();
      this.carousel = null;
    }

    window.removeEventListener(
      "resize",
      debounce(this.handleResizeEvents, 250),
    );
  }

  static get observedAttributes(): string[] {
    return ["autoplay", "autoplay-speed", "pagination", "arrows"];
  }

  attributeChangedCallback(): void {
    this.initializeConfig();
    this.createCarousel();
  }

  initializeConfig(): void {
    // Checks for reduced motion preferences
    const reduceMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const prefersReducedMotion = reduceMotionQuery.matches;
    const customConfig = JSON.parse(
      this.getAttribute("setting-custom-config") || "{}",
    );

    this.config = {
      autoplay:
        !prefersReducedMotion &&
        this.getAttribute("setting-autoplay") === "true",
      interval:
        parseInt(this.getAttribute("setting-autoplay-speed") || "5") * 1000,
      pagination: this.getAttribute("setting-pagination") === "true",
      arrows: this.getAttribute("setting-arrows") === "true",
      perPage: parseInt(this.getAttribute("setting-per-page") || "4"),
      padding: {
        right:
          this.getAttribute("setting-peak-next-desktop") === "true" ? "10%" : 0,
      },
      breakpoints: {
        640: {
          perPage: parseInt(this.getAttribute("per-page-mobile") || "1"),
          gap: parseInt(this.getAttribute("setting-gap-mobile") || "8"),
          padding: {
            right:
              this.getAttribute("setting-peak-next-mobile") === "true"
                ? "20%"
                : 0,
          },
        },
      },
      gap: parseInt(this.getAttribute("setting-gap") || "16"),
      rewind: false,
      type: "loop",
      drag: this.getAttribute("setting-draggable") === "true" ? true : false,
      perMove: parseInt(this.getAttribute("per-move") || "1"),
      classes: {
        arrows:
          this.getAttribute("setting-arrows") === "true"
            ? "uwp-carousel__arrows"
            : "",
        arrow:
          this.getAttribute("setting-arrows") === "true"
            ? "uwp-carousel__arrow"
            : "",
        prev:
          this.getAttribute("setting-arrows") === "true"
            ? "uwp-carousel__arrow--prev"
            : "",
        next:
          this.getAttribute("setting-arrows") === "true"
            ? "uwp-carousel__arrow--next"
            : "",
      },
    };
    this.config = { ...this.config, ...customConfig };
  }

  createCarousel(): void {
    if (this.carousel) {
      this.carousel.destroy();
      this.carousel = null;
    }

    this.carousel = new Splide(this, this.config);

    this.setupCarouselEvents();
    this.carousel.mount();
  }

  setupCarouselEvents(): void {
    if (!this.carousel) {
      return;
    }

    this.carousel.on("mounted move", () => {
      const scrollBar = this.querySelector(
        "[js-carousel-controls='scrollbarProgress']",
      ) as HTMLElement;

      if (!scrollBar || !this.carousel) {
        return;
      }

      const end = this.carousel.Components.Controller.getEnd() + 1;
      const rate = Math.min((this.carousel.index + 1) / end, 1);
      scrollBar.style.width = String(100 * rate) + "%";
      
      // Hide arrows if all slides fit on screen (no need to navigate)
      const totalSlides = this.carousel.length;
      const perPage = this.carousel.options.perPage || 1;
      const arrows = this.querySelector(".splide__arrows, .uwp-carousel__arrows, .carousel-controls__arrow-wrapper") as HTMLElement;
      
      if (arrows && totalSlides <= perPage) {
        arrows.style.display = "none";
      } else if (arrows) {
        arrows.style.display = "";
      }
    });

    window.addEventListener("resize", debounce(this.handleResizeEvents, 250));
  }

  handleResizeEvents(): void {
    const stopDesktopCarousel =
      this.getAttribute("setting-stop-desktop") === "true";

    if (stopDesktopCarousel) {
      const desktopBreakpoint = 640;

      if (window.matchMedia(`(min-width: ${desktopBreakpoint}px)`).matches) {
        this.carousel?.destroy();
      } else {
        this.createCarousel();
      }
    }
  }

  validateMarkup(): void {
    const track = this.querySelector(".splide__track");
    const list = this.querySelector(".splide__list");
    const slides = this.querySelectorAll(".splide__slide");

    if (!track || !list || slides.length === 0) {
      console.error(
        "UWPCarousel requires .splide__track, .splide__list, and .splide__slide elements.",
      );
      throw new Error(
        "Markup validation error: Missing required carousel elements.",
      );
    }
  }
}
